import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, User, Phone } from 'lucide-react';

import './Login.css';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      toast.error('Mohon lengkapi seluruh kolom');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Konfirmasi kata sandi tidak cocok');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Kata sandi minimal 6 karakter');
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: 'penghuni' // Selalu daftar sebagai penghuni
      });
      toast.success('Pendaftaran berhasil! Silakan tunggu konfirmasi dari Tim Kos Cemara.');
      navigate('/login');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Gagal mendaftar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="cemara-login">
      <div className="cemara-login-shell">
        <Link to="/" className="cemara-login-brand" aria-label="Kembali ke halaman utama Kos Cemara">
          <img src="/logo-original.png" alt="Logo Kos Cemara" width="52" height="65" />
          <div><span className="cemara-login-name">Kos Cemara</span><p>Selamat datang di rumah Anda.</p></div>
        </Link>
        <section className="cemara-login-card" aria-labelledby="register-heading">
          <div className="cemara-login-intro">
            <span className="cemara-login-eyebrow">AKUN KOS CEMARA</span>
            <h1 id="register-heading">Daftar penghuni</h1>
            <p>Buat akun untuk mengakses informasi hunian dan layanan Kos Cemara.</p>
          </div>
            <form onSubmit={handleSubmit} className="space-y-4 mt-7">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">Nama lengkap</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name" autoComplete="name" required
                    placeholder="Nama lengkap"
                    value={formData.name}
                    onChange={handleChange}
                    className="cemara-login-input h-12 pl-12"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Alamat email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required
                    type="email"
                    placeholder="Masukkan alamat email"
                    value={formData.email}
                    onChange={handleChange}
                    className="cemara-login-input h-12 pl-12"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Nomor telepon</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="phone"
                    name="phone" type="tel" autoComplete="tel" required
                    placeholder="08xxxxxxxxxx"
                    value={formData.phone}
                    onChange={handleChange}
                    className="cemara-login-input h-12 pl-12"
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Kata sandi</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password" autoComplete="new-password" required
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimal 6 karakter"
                    value={formData.password}
                    onChange={handleChange}
                    className="cemara-login-input h-12 pl-12 pr-12"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
                    aria-pressed={showPassword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">Konfirmasi kata sandi</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword" autoComplete="new-password" required
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ulangi kata sandi"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="cemara-login-input h-12 pl-12"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#f5f7f1] border border-[#e2e8d9]">
                <p className="text-xs text-[#5c6e54] leading-relaxed">
                  Pendaftaran Anda akan ditinjau oleh Tim Kos Cemara sebelum akun dapat digunakan.
                </p>
              </div>

              <Button
                type="submit"
                className="cemara-login-submit w-full h-12 font-medium text-base mt-2"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  'Sedang mendaftar…'
                ) : (
                  'Buat akun'
                )}
              </Button>
            </form>

            <p className="cemara-login-register">
              Sudah memiliki akun?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                Masuk
              </Link>
            </p>
        </section>
        <footer className="cemara-login-footer">
          <p>Butuh bantuan mendaftar? <a href="mailto:koscemara22@gmail.com">Hubungi kami</a></p>
          <span>&copy; {new Date().getFullYear()} Kos Cemara. Semua hak dilindungi.</span>
        </footer>
      </div>
    </main>
  );
};

export default Register;
